import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { NEVER_DIRTY, OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { PaymentReview } from '@client/components/claims/financials/payment-approval/PaymentReview';
import { isPaymentUnavailable } from '@client/components/claims/financials/payment-approval/payment-availability';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpcOptions } from '@client/trpc/client';
import { ENABLE_CLAIM_PAYMENT_APPROVAL_FLAG } from '@shared/constants';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

/**
 * CUR-2673: a payment raised for approval, on the claim's Financials tab. The person it waits on
 * decides it here; the raiser withdraws it or marks it paid; anyone else who may see it sees where it
 * is, without the buttons.
 */
export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/payments/$compensationId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Payment',
        },
        // Hidden with the rest of payment approval; the server answers NOT_FOUND while it is off.
        featureFlag: ENABLE_CLAIM_PAYMENT_APPROVAL_FLAG,
    },
    params: {
        parse({ compensationId }) {
            return {
                compensationId: BigInt(compensationId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <PaymentPanel />
        </PageController>
    );
}

function PaymentPanel() {
    const { id: claimId, compensationId } = Route.useParams();
    const navigate = useNavigate();
    const { data, error, refetch, isFetching } = useQuery(
        trpcOptions.claimAuthority.getPayment.queryOptions({ compensationId }, { meta: { errorSurface: 'inline' } }),
    );
    // A refetch the viewer may no longer see (or with payment approval turned off) answers not-found:
    // drop the cached review and its buttons.
    // A payment on another claim than the one in the URL is not decided under this claim's header.
    const onAnotherClaim = data !== undefined && data.claim.id !== claimId;
    const review = (error && isPaymentUnavailable(error)) || onAnotherClaim ? undefined : data;

    return (
        <OverlayPage
            title="Payment"
            size="small"
            isDirty={NEVER_DIRTY}
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => <DrawerFooter onCancel={onClose} cancelText="Close" showOk={false} />}
        >
            {({ onClose }) =>
                review ? (
                    // One review per payment: moving on to another payment (through the browser's history)
                    // drops the previous one's follow-up, so its decision cannot close this one.
                    <PaymentReview key={review.id.toString()} review={review} onDecided={onClose} />
                ) : onAnotherClaim ? (
                    <p className="m-0 text-muted-foreground text-sm">This payment is not on this claim.</p>
                ) : error ? (
                    <InlineQueryError
                        error={error}
                        onRetry={() => void refetch()}
                        retrying={isFetching}
                        description="Could not load this payment."
                    />
                ) : (
                    <Loading />
                )
            }
        </OverlayPage>
    );
}
